import type {
  TpStripePremiumYearDiscountInfo,
  TpStripePremiumYearInfo,
  TpStripeSubscription,
} from '@features/Stripe/types';
import { atom } from 'jotai';
import { atomWithReset, atomWithStorage, createJSONStorage } from 'jotai/utils';

type TpStripeUser = {
  email: string;
  token: string;
  customerId: string;
  campaign: string;
  userId: number | string;
  accessCode: string;
};
export const userAtom = atomWithStorage<TpStripeUser>('stripeUser', {
  ...createJSONStorage(() => localStorage),
  email: '',
  token: '',
  customerId: '',
  campaign: '',
  userId: 0,
  accessCode: '',
});

export const selectedPlanAtom = atomWithReset<TpStripeSubscription>({
  academy_price_id: '',
  bundle: false,
  billingPeriod: 0,
  billingPeriodUnit: '',
  braintreeMerchantId: null,
  braintreePlanId: null,
  currency: 'usd',
  description: null,
  discountPercentage: null,
  displayPrice: '',
  fractionalPrice: 0,
  fractionalPriceDiscount: null,
  fullPrice: 0,
  fullPriceDiscount: 0,
  gift: false,
  hideDescription: false,
  id: '',
  introOffer: null,
  learnMoreList: [],
  name: '',
  noMoneyDiscount: false,
  order: 0,
  paddlePlanId: undefined,
  planName: '',
  preselected: false,
  priceAfterIntroOffer: null,
  priceId: '',
  specialDiscountPercentage: null,
  specialOffer: false,
  introOfferData: {
    billingPeriod: 0,
    billingPeriodUnit: '',
    currency: '',
    displayPrice: '',
    fractionalPrice: 0,
    fractionalPriceDiscount: 0,
    fullPrice: 0,
    fullPriceDiscount: 0,
    introOfferId: '',
    introOfferDuration: '',
    introOfferDescription: '',
    planId: '',
  },
});

export const premiumYearAllDataAtom = atomWithStorage<{
  premiumYearData: TpStripePremiumYearInfo;
  premiumYearDiscountData: TpStripePremiumYearDiscountInfo;
}>('stripePremiumYearData', {
  ...createJSONStorage(() => localStorage),
  premiumYearData: {
    currency: 'usd',
    fullPriceDiscount: 0,
    fullPrice: 0,
    discountPercentage: 0,
    braintreePlanId: '',
    upgradeId: '',
  },
  premiumYearDiscountData: {
    currency: 'usd',
    fullPriceDiscount: 0,
    fullPrice: 0,
    discountPercentage: 0,
    braintreePlanId: '',
    upgradeId: '',
  },
});

export const upsellInfoAtom = atomWithStorage('stripeUpsellInfo', {
  ...createJSONStorage(() => localStorage),
  currency: 'USD',
  discountPercentage: 0,
  fullPrice: 0,
  fullPriceDiscount: 0,
});

export const upgradeInfoAtom = atomWithStorage('stripeUpgradeInfo', {
  ...createJSONStorage(() => localStorage),
  currency: 'USD',
  discountPercentage: 0,
  fullPrice: 0,
  fullPriceDiscount: 0,
  upgradeId: '',
  braintreePlanId: '',
});

export const processingModalStatusAtom = atom(false);
export const errorModalStatusAtom = atom(false);

export const stripeWasPaymentTriedAtom = atomWithStorage(
  'stripeWasPaymentTried',
  {
    ...createJSONStorage(() => localStorage),
    oneTimeOfferShow: false,
    wasTried: false,
    showCrossSailOffer: false,
    wasUpsellSailOfferShown: false,
    isSpecialOfferFlow: false,
  },
);

export const stripeSubscription = atom(
  typeof window !== 'undefined' &&
    (localStorage.getItem('stripeActiveSubscription') ?? ''),
);

export const stripeSubscriptionWithPersistence = atom(
  (get) => get(stripeSubscription),
  (get, set, newString: string) => {
    set(stripeSubscription, newString);
    localStorage.setItem('stripeActiveSubscription', newString);
  },
);

export const paymentMethod = atom(
  typeof window !== 'undefined' &&
    (localStorage.getItem('btPaymentMethod') ?? ''),
);

export const paymentMethodWithPersistence = atom(
  (get) => get(paymentMethod),
  (get, set, newString: string) => {
    set(paymentMethod, newString);
    localStorage.setItem('btPaymentMethod', newString);
  },
);

export const stripePaymentErrorAtom = atomWithStorage('stripePaymentError', {
  ...createJSONStorage(() => sessionStorage),
  wasPaymentDeclinedOfferAccepted: false,
  errorCode: '',
  declineCode: '',
  paymentSystem: '',
  paymentMethod: '',
});

export const stripePaymentRetryAtom = atomWithStorage(
  'stripePaymentRetry',
  {
    retryModalShownCount: 0,
    retryPaymentErrorKey: '',
    sameRetryablePaymentErrorRetryCount: 0,
  },
  createJSONStorage(() => sessionStorage),
);

export const stripeTryAnotherCardAtom = atomWithStorage(
  'stripeTryAnotherCard',
  {
    showModal: false,
    planId: '',
    paymentMethod: '',
    tryAnotherCardModalShownCount: 0,
    tryAnotherCardTappedCount: 0,
    clearCardFormCount: 0,
  },
  createJSONStorage(() => sessionStorage),
);

export const stripePaymentDeclinedOfferAtom = atomWithStorage(
  'stripePaymentDeclinedOffer',
  {
    paymentDeclinedShownCount: 0,
    paymentDeclinedTappedCount: 0,
  },
  createJSONStorage(() => sessionStorage),
);

export const userPaymentsInfoAtom = atomWithStorage('stripePaymentError', {
  ...createJSONStorage(() => localStorage),
  wasCheapOfferBought: false,
  wasStandardPlanBought: false,
  paymentMethod: '',
  paymentSystem: '',
  subscriptionId: '',
  btSubscriptionId: '',
  btCustomerId: '',
  wasSubscriptionModified: false,
});

export const selectedPaymentMethodTabAtom = atom<{
  paymentMethod:
    | 'PayPal'
    | 'Google Pay'
    | 'Apple Pay'
    | 'Credit card'
    | undefined;
}>({ paymentMethod: undefined });
